/**
 * Custom Captcha Component
 *
 * A self-hosted math-based captcha that protects against bot abuse.
 * No external dependencies - fully integrated with our backend.
 */

/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, AlertTriangle, Loader2, RefreshCw } from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8002";

interface CaptchaChallenge {
  challenge_id: string;
  question: string;
  image_base64: string | null;
  expires_in_seconds: number;
}

interface CaptchaProps {
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  challengeType?: "math" | "text";
}

export function Captcha({
  onVerify,
  onError,
  challengeType = "math",
}: CaptchaProps) {
  const [challenge, setChallenge] = useState<CaptchaChallenge | null>(null);
  const [answer, setAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState(3);

  const fetchChallenge = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setAnswer("");
    setAttemptsLeft(3);

    try {
      const response = await fetch(
        `${API_BASE}/api/v1/captcha/generate?challenge_type=${challengeType}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate captcha");
      }

      const data: CaptchaChallenge = await response.json();
      setChallenge(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load captcha";
      setError(message);
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  }, [challengeType, onError]);

  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  const handleVerify = async () => {
    if (!challenge || !answer.trim()) return;

    setIsVerifying(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/v1/captcha/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challenge_id: challenge.challenge_id,
          answer: answer.trim(),
        }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        onVerify(data.token);
      } else {
        // Extract attempts remaining from message
        const match = data.message.match(/(\d+) attempts? remaining/);
        if (match) {
          setAttemptsLeft(parseInt(match[1], 10));
        }

        if (
          data.message.includes("expired") ||
          data.message.includes("invalid") ||
          attemptsLeft <= 1
        ) {
          // Need a new challenge
          setError("Challenge expired. Loading new one...");
          setTimeout(fetchChallenge, 1000);
        } else {
          setError(data.message);
          setAnswer("");
        }
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Verification failed";
      setError(message);
      onError?.(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isVerifying && answer.trim()) {
      handleVerify();
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 p-5 bg-gradient-to-b from-amber-50 to-orange-50 border border-amber-200 rounded-xl shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 text-amber-700">
        <ShieldCheck className="w-5 h-5" />
        <span className="font-medium">Please verify you're human</span>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center gap-2 text-gray-500 py-4">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading challenge...</span>
        </div>
      )}

      {/* Challenge display */}
      {!isLoading && challenge && (
        <div className="flex flex-col items-center gap-4 w-full max-w-xs">
          {/* Captcha image or text */}
          <div className="relative">
            {challenge.image_base64 ? (
              <img
                src={`data:image/png;base64,${challenge.image_base64}`}
                alt="Captcha challenge"
                className="rounded-lg border border-gray-200 shadow-inner"
                draggable={false}
              />
            ) : (
              <div className="px-6 py-4 bg-white rounded-lg border border-gray-200 shadow-inner">
                <span className="text-2xl font-mono font-bold text-gray-700 tracking-wider select-none">
                  {challenge.question.replace(" = ?", "")}
                </span>
              </div>
            )}

            {/* Refresh button */}
            <button
              onClick={fetchChallenge}
              className="absolute -top-2 -right-2 p-1.5 bg-white rounded-full border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors"
              title="Get new challenge"
              disabled={isLoading}
            >
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Question text for math */}
          {challengeType === "math" && (
            <p className="text-sm text-gray-600">
              Solve: <span className="font-medium">{challenge.question}</span>
            </p>
          )}

          {/* Answer input */}
          <div className="w-full">
            <input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                challengeType === "math" ? "Enter your answer" : "Type the text"
              }
              className="w-full px-4 py-2.5 text-center text-lg font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              disabled={isVerifying}
              autoComplete="off"
              autoFocus
            />
          </div>

          {/* Verify button */}
          <button
            onClick={handleVerify}
            disabled={!answer.trim() || isVerifying}
            className="w-full px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify"
            )}
          </button>

          {/* Attempts remaining */}
          {attemptsLeft < 3 && (
            <p className="text-sm text-gray-500">
              {attemptsLeft} attempt{attemptsLeft !== 1 ? "s" : ""} remaining
            </p>
          )}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Footer text */}
      <p className="text-xs text-gray-500 text-center max-w-xs">
        This helps protect our service from automated abuse.
      </p>
    </div>
  );
}

/**
 * Hook to manage captcha state in chat
 */
export function useCaptcha() {
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
    setCaptchaRequired(false);
  };

  const handleRateLimitError = (error: {
    captcha_required?: boolean;
    error?: string;
    retry_after_seconds?: number;
  }) => {
    if (error.captcha_required) {
      setCaptchaRequired(true);
      setCaptchaToken(null);
    }
    return error;
  };

  const resetCaptcha = () => {
    setCaptchaRequired(false);
    setCaptchaToken(null);
  };

  return {
    captchaRequired,
    captchaToken,
    handleCaptchaVerify,
    handleRateLimitError,
    resetCaptcha,
  };
}

export default Captcha;
